<template>
    <b-modal
        v-model="visible"
        size="xl"
        centered
        content-class="argus-glass-dialog"
        scrollable
        title="Сопоставить человека с карточкой сотрудника"
        :hide-header-close="saving"
        :no-close-on-backdrop="saving"
        :no-close-on-esc="saving"
        @shown="start"
        @hidden="reset"
    >
        <section v-if="line" class="unresolved-resolver">
            <div class="unresolved-resolver__incident" role="alert">
                <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
                <div>
                    <strong>Кадровый инцидент: у человека нет подтверждённой карточки</strong>
                    <p>
                        Пока строка <b>{{ line.employee_name_snapshot }}</b> не сопоставлена,
                        табель нельзя отправить на проверку.
                    </p>
                </div>
            </div>

            <section class="unresolved-resolver__step">
                <header>
                    <span>1</span>
                    <div>
                        <h3>Выберите настоящую карточку сотрудника</h3>
                        <p>Проверьте полное ФИО. Система понимает фамилию с инициалами.</p>
                    </div>
                </header>

                <div v-if="line.resolution_suggestion" class="unresolved-resolver__suggestion">
                    <i class="fas fa-lightbulb" aria-hidden="true"></i>
                    <div>
                        <strong>Возможно, это {{ line.resolution_suggestion.employee_name }}</strong>
                        <span>Карточка появилась в этом табеле после автоматического прибытия.</span>
                    </div>
                </div>

                <div class="unresolved-resolver__search">
                    <b-form-input
                        v-model.trim="query"
                        placeholder="Например: Гвоздь С.К."
                        :disabled="searching || saving"
                        @keyup.enter="search"
                    ></b-form-input>
                    <b-button variant="primary" :disabled="query.length < 2 || searching || saving" @click="search">
                        <b-spinner v-if="searching" small></b-spinner>
                        <template v-else>
                            <i class="fas fa-search mr-1" aria-hidden="true"></i>
                            Найти карточку
                        </template>
                    </b-button>
                </div>

                <div
                    v-if="visible && searched && !searching && !errorMessage && !results.length"
                    class="unresolved-resolver__missing-card"
                    role="alert"
                    aria-atomic="true"
                >
                    <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
                    <div>
                        <strong class="unresolved-resolver__missing-card-title">Карточка сотрудника не найдена</strong>
                        <ol>
                            <li>Проверьте, правильно ли указано <b>ФИО</b>.</li>
                        </ol>
                        <TimesheetEmployeeCardContacts :key="`${timesheetId}-${line.id}-${contactSearchVersion}`" :timesheet-id="timesheetId" />
                    </div>
                </div>

                <div v-if="results.length" class="unresolved-resolver__results">
                    <button
                        v-for="person in results"
                        :key="person.employee_id"
                        type="button"
                        :class="{'is-selected': selected && selected.employee_id === person.employee_id}"
                        :disabled="loadingPreview || saving"
                        @click="selectEmployee(person)"
                    >
                        <span class="unresolved-resolver__result-check">
                            <i
                                :class="selected && selected.employee_id === person.employee_id
                                    ? 'fas fa-check-circle'
                                    : 'far fa-circle'"
                                aria-hidden="true"
                            ></i>
                        </span>
                        <span>
                            <strong>{{ person.full_name }}</strong>
                            <small>{{ person.position || lifecycleLabel(person.lifecycle_status) }}</small>
                        </span>
                        <b-badge class="timesheet-status-badge" v-if="person.already_in_timesheet" variant="info">
                            Уже есть отдельной строкой в этом табеле
                        </b-badge>
                    </button>
                </div>
            </section>

            <section v-if="selected" class="unresolved-resolver__step">
                <header>
                    <span>2</span>
                    <div>
                        <h3>Сверьте и объедините строки</h3>
                        <p>Одинаковые данные объединятся сами. Красные расхождения требуют вашего выбора.</p>
                    </div>
                </header>

                <div class="unresolved-resolver__identity-check">
                    <div>
                        <small>Было внесено вручную</small>
                        <strong>{{ line.employee_name_snapshot }}</strong>
                    </div>
                    <i class="fas fa-arrow-right" aria-hidden="true"></i>
                    <div>
                        <small>Карточка сотрудника</small>
                        <strong>{{ selected.full_name }}</strong>
                    </div>
                </div>

                <div v-if="loadingPreview" class="unresolved-resolver__loading">
                    <b-spinner></b-spinner>
                    <span>Сравниваем часы, КТУ и характеристику…</span>
                </div>

                <template v-else-if="preview">
                    <b-alert v-if="preview.target_line_id" show variant="info" class="mt-3">
                        У карточки уже есть строка в этом табеле. После подтверждения две строки станут одной.
                    </b-alert>

                    <div class="unresolved-resolver__section-heading">
                        <h4>Отметки по дням</h4>
                        <span v-if="dayConflicts.length" class="is-conflict">
                            Нужно выбрать: {{ dayConflicts.length }}
                        </span>
                        <span v-else class="is-ready">Расхождений нет</span>
                    </div>
                    <div v-if="!preview.days.length" class="unresolved-resolver__empty">
                        Отметок по дням ещё нет.
                    </div>
                    <div v-else class="unresolved-resolver__comparison-list">
                        <article
                            v-for="day in preview.days"
                            :key="day.date"
                            :class="{'is-conflict': day.state === 'conflict'}"
                        >
                            <div class="unresolved-resolver__comparison-title">
                                <strong>{{ formatDate(day.date) }}</strong>
                                <span :class="stateClass(day.state)">{{ stateLabel(day.state) }}</span>
                            </div>
                            <div v-if="day.state === 'conflict'" class="unresolved-resolver__choices">
                                <button
                                    type="button"
                                    :class="{'is-selected': dayChoices[day.date] === 'unresolved'}"
                                    :disabled="saving"
                                    @click="$set(dayChoices, day.date, 'unresolved')"
                                >
                                    <small>Оставить из ручной строки</small>
                                    <strong>{{ dayValue(day.unresolved) }}</strong>
                                </button>
                                <button
                                    type="button"
                                    :class="{'is-selected': dayChoices[day.date] === 'employee'}"
                                    :disabled="saving"
                                    @click="$set(dayChoices, day.date, 'employee')"
                                >
                                    <small>Оставить из карточки</small>
                                    <strong>{{ dayValue(day.employee) }}</strong>
                                </button>
                            </div>
                            <div v-else class="unresolved-resolver__automatic-value">
                                Сохранится: <strong>{{ automaticDayValue(day) }}</strong>
                            </div>
                        </article>
                    </div>

                    <div class="unresolved-resolver__section-heading">
                        <h4>КТУ и основания</h4>
                        <span :class="stateClass(preview.ktu.state)">{{ stateLabel(preview.ktu.state) }}</span>
                    </div>
                    <div v-if="preview.ktu.state === 'conflict'" class="unresolved-resolver__choices">
                        <button
                            type="button"
                            :class="{'is-selected': ktuChoice === 'unresolved'}"
                            :disabled="saving"
                            @click="ktuChoice = 'unresolved'"
                        >
                            <small>Из ручной строки</small>
                            <strong>{{ ktuValue(preview.ktu.unresolved) }}</strong>
                        </button>
                        <button
                            type="button"
                            :class="{'is-selected': ktuChoice === 'employee'}"
                            :disabled="saving"
                            @click="ktuChoice = 'employee'"
                        >
                            <small>Из карточки</small>
                            <strong>{{ ktuValue(preview.ktu.employee) }}</strong>
                        </button>
                    </div>
                    <div v-else class="unresolved-resolver__automatic-value">
                        Сохранится: <strong>{{ automaticValue(preview.ktu, ktuValue) }}</strong>
                    </div>

                    <div class="unresolved-resolver__section-heading">
                        <h4>Характеристика</h4>
                        <span :class="stateClass(preview.characteristic.state)">
                            {{ stateLabel(preview.characteristic.state) }}
                        </span>
                    </div>
                    <div
                        v-if="preview.characteristic.state === 'conflict'"
                        class="unresolved-resolver__choices"
                    >
                        <button
                            type="button"
                            :class="{'is-selected': characteristicChoice === 'unresolved'}"
                            :disabled="saving"
                            @click="characteristicChoice = 'unresolved'"
                        >
                            <small>Из ручной строки</small>
                            <span
                                v-if="characteristicItems(preview.characteristic.unresolved).length"
                                class="unresolved-resolver__characteristics"
                            >
                                <span
                                    v-for="item in characteristicItems(preview.characteristic.unresolved)"
                                    :key="`unresolved-characteristic-${item.key}`"
                                >
                                    <b>{{ item.label }}</b>
                                    <strong :class="item.className">{{ item.value }}</strong>
                                </span>
                            </span>
                            <strong v-else>Характеристика не заполнена</strong>
                        </button>
                        <button
                            type="button"
                            :class="{'is-selected': characteristicChoice === 'employee'}"
                            :disabled="saving"
                            @click="characteristicChoice = 'employee'"
                        >
                            <small>Из карточки</small>
                            <span
                                v-if="characteristicItems(preview.characteristic.employee).length"
                                class="unresolved-resolver__characteristics"
                            >
                                <span
                                    v-for="item in characteristicItems(preview.characteristic.employee)"
                                    :key="`employee-characteristic-${item.key}`"
                                >
                                    <b>{{ item.label }}</b>
                                    <strong :class="item.className">{{ item.value }}</strong>
                                </span>
                            </span>
                            <strong v-else>Характеристика не заполнена</strong>
                        </button>
                    </div>
                    <div v-else class="unresolved-resolver__automatic-value">
                        <span>Будет сохранено без вашего выбора:</span>
                        <div
                            v-if="characteristicItems(automaticRawValue(preview.characteristic)).length"
                            class="unresolved-resolver__characteristics"
                        >
                            <span
                                v-for="item in characteristicItems(automaticRawValue(preview.characteristic))"
                                :key="`automatic-characteristic-${item.key}`"
                            >
                                <b>{{ item.label }}</b>
                                <strong :class="item.className">{{ item.value }}</strong>
                            </span>
                        </div>
                        <strong v-else>Характеристика не заполнена</strong>
                    </div>

                </template>
            </section>
        </section>
        <template #modal-footer>
            <div class="w-100">
                <b-alert v-if="errorMessage" show variant="danger" role="alert" aria-atomic="true">
                    {{ errorMessage }}
                </b-alert>
                <div v-if="preview" class="unresolved-resolver__finish">
                    <div>
                        <strong v-if="unselectedConflictCount" class="text-danger">Осталось выбрать: {{ unselectedConflictCount }}</strong>
                        <strong v-else class="text-success">Всё готово к объединению</strong>
                        <small>Исходные данные сохранятся в истории. Объединение можно отменить.</small>
                    </div>
                    <b-button variant="success" :disabled="unselectedConflictCount > 0 || saving" @click="resolve">
                        <b-spinner v-if="saving" small></b-spinner>
                        <template v-else>
                            <i class="fas fa-check mr-1" aria-hidden="true"></i>
                            Подтвердить: это один человек
                        </template>
                    </b-button>
                </div>
            </div>
        </template>
    </b-modal>
</template>

<script>
import api from '@/utils/api';
import TimesheetEmployeeCardContacts from './TimesheetEmployeeCardContacts.vue';

export default {
    name: 'TimesheetUnresolvedResolver',
    components: {TimesheetEmployeeCardContacts},
    props: {
        value: {type: Boolean, default: false},
        timesheetId: {type: [Number, String], default: null},
        line: {type: Object, default: null},
        codes: {type: Array, default: () => []},
        policy: {type: Object, default: () => ({})},
        resolveWithArrivals: {type: Function, required: true}
    },
    data() {
        return {
            query: '',
            results: [],
            searched: false,
            searching: false,
            selected: null,
            preview: null,
            loadingPreview: false,
            saving: false,
            errorMessage: '',
            dayChoices: {},
            ktuChoice: null,
            characteristicChoice: null,
            startedForLineId: null,
            contactSearchVersion: 0
        };
    },
    computed: {
        visible: {
            get() { return this.value; },
            set(value) { this.$emit('input', value); }
        },
        codeById() {
            return this.codes.reduce((result, code) => {
                result[Number(code.id)] = code;
                return result;
            }, {});
        },
        dayConflicts() {
            return this.preview ? this.preview.days.filter(day => day.state === 'conflict') : [];
        },
        unselectedConflictCount() {
            if (!this.preview) return 0;
            let count = this.dayConflicts.filter(day => !this.dayChoices[day.date]).length;
            if (this.preview.ktu.state === 'conflict' && !this.ktuChoice) count += 1;
            if (this.preview.characteristic.state === 'conflict' && !this.characteristicChoice) count += 1;
            return count;
        }
    },
    methods: {
        reset() {
            this.startedForLineId = null;
        },
        async start() {
            if (!this.line || this.startedForLineId === Number(this.line.id)) return;
            this.startedForLineId = Number(this.line.id);
            this.query = this.line.employee_name_snapshot || '';
            this.results = [];
            this.searched = false;
            this.selected = null;
            this.preview = null;
            this.errorMessage = '';
            this.dayChoices = {};
            this.ktuChoice = null;
            this.characteristicChoice = null;
            await this.search();
            const suggestion = this.line.resolution_suggestion;
            if (!suggestion) return;
            const candidate = this.results.find(person => (
                Number(person.employee_id) === Number(suggestion.employee_id)
            )) || {
                employee_id: Number(suggestion.employee_id),
                full_name: suggestion.employee_name,
                position: suggestion.position,
                already_in_timesheet: true
            };
            await this.selectEmployee(candidate);
        },
        async search() {
            if (!this.timesheetId || this.query.length < 2 || this.saving) return;
            this.contactSearchVersion += 1;
            this.searching = true;
            this.errorMessage = '';
            try {
                const response = await api.get(`/api/timesheets/${this.timesheetId}/people/search`, {
                    params: {q: this.query}
                });
                this.results = response.data && response.data.employees || [];
                this.searched = true;
            } catch (error) {
                this.errorMessage = this.responseMessage(error, 'Не удалось найти карточку сотрудника.');
            } finally {
                this.searching = false;
            }
        },
        async selectEmployee(person) {
            if (this.saving) return;
            this.selected = person;
            this.preview = null;
            this.dayChoices = {};
            this.ktuChoice = null;
            this.characteristicChoice = null;
            this.loadingPreview = true;
            this.errorMessage = '';
            try {
                const response = await api.get(
                    `/api/timesheets/${this.timesheetId}/lines/${this.line.id}/unresolved-resolution-preview`,
                    {params: {employee_id: person.employee_id}}
                );
                this.preview = response.data;
            } catch (error) {
                this.errorMessage = this.responseMessage(error, 'Не удалось сравнить строки табеля.');
            } finally {
                this.loadingPreview = false;
            }
        },
        async resolve() {
            if (!this.preview || this.unselectedConflictCount || this.saving) return;
            this.saving = true;
            this.errorMessage = '';
            try {
                const response = await this.resolveWithArrivals(
                    this.timesheetId, this.line.id,
                    {
                        employee_id: this.selected.employee_id,
                        day_choices: {...this.dayChoices},
                        ktu_choice: this.ktuChoice,
                        characteristic_choice: this.characteristicChoice
                    }
                );
                this.$emit('resolved', response.data);
                this.visible = false;
            } catch (error) {
                this.errorMessage = this.responseMessage(error, 'Не удалось сопоставить строки.');
            } finally {
                this.saving = false;
            }
        },
        responseMessage(error, fallback) {
            const data = error && error.response && error.response.data;
            return data && (data.msg || data.message || data.error) || error && error.message || fallback;
        },
        lifecycleLabel(status) {
            return {active: 'Действующий сотрудник', reserve: 'Резерв', unverified: 'Карточка не проверена'}[status]
                || 'Карточка сотрудника';
        },
        stateLabel(state) {
            return {
                empty: 'Данных нет',
                unresolved_only: 'Сохраним данные из ручной строки',
                employee_only: 'Сохраним данные из карточки сотрудника',
                same: 'Данные одинаковые',
                conflict: 'ДАННЫЕ РАЗНЫЕ — ВЫБЕРИТЕ ВАРИАНТ'
            }[state] || state;
        },
        stateClass(state) {
            return state === 'conflict' ? 'is-conflict' : 'is-ready';
        },
        formatDate(value) {
            const parts = String(value || '').split('-');
            return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : value;
        },
        dayValue(value) {
            if (!value) return 'пусто';
            const parts = [];
            if (Number(value.worked_minutes) > 0) parts.push(`${Number(value.worked_minutes) / 60} ч`);
            ['absence_code_id', 'travel_code_id', 'other_code_id'].forEach(key => {
                const code = this.codeById[Number(value[key])];
                if (code) parts.push(`${code.code} — ${code.name}`);
            });
            if (value.note) parts.push(`Примечание: ${value.note}`);
            return parts.join(' · ') || 'пусто';
        },
        automaticDayValue(day) {
            if (day.state === 'unresolved_only') return this.dayValue(day.unresolved);
            return this.dayValue(day.employee || day.unresolved);
        },
        automaticValue(section, formatter) {
            if (section.state === 'unresolved_only') return formatter(section.unresolved);
            return formatter(section.employee || section.unresolved);
        },
        automaticRawValue(section) {
            if (section.state === 'unresolved_only') return section.unresolved;
            return section.employee || section.unresolved;
        },
        ktuValue(value) {
            if (!value || (value.ktu === null && !(value.ktu_evidence || []).length)) return 'не заполнено';
            const parts = [value.ktu === null ? 'КТУ не указан' : `КТУ ${value.ktu}`];
            if ((value.ktu_evidence || []).length) parts.push(`оснований: ${value.ktu_evidence.length}`);
            return parts.join(', ');
        },
        characteristicItems(value) {
            const source = value && typeof value === 'object' ? value : {};
            const fields = this.policy.characteristic_fields || [];
            const fieldsByKey = fields.reduce((result, field) => {
                result[field.key] = field;
                return result;
            }, {});
            const configuredKeys = fields.map(field => field.key).filter(key => (
                Object.prototype.hasOwnProperty.call(source, key)
            ));
            const extraKeys = Object.keys(source).filter(key => !fieldsByKey[key]);
            return configuredKeys.concat(extraKeys)
                .filter(key => source[key] !== null && source[key] !== '')
                .map(key => {
                    const rawValue = source[key];
                    let displayValue = rawValue;
                    let className = '';
                    if (rawValue === true) {
                        displayValue = 'Да';
                        className = 'is-yes';
                    } else if (rawValue === false) {
                        displayValue = 'Нет';
                        className = 'is-no';
                    } else if (Array.isArray(rawValue)) {
                        displayValue = rawValue.join(', ');
                    } else if (rawValue && typeof rawValue === 'object') {
                        displayValue = Object.values(rawValue).join(', ');
                    }
                    return {
                        key,
                        label: fieldsByKey[key] && fieldsByKey[key].label || key,
                        value: String(displayValue),
                        className
                    };
                });
        }
    }
};
</script>

<style scoped lang="scss">
.unresolved-resolver { color: #1f2937; }
.unresolved-resolver__incident {
    display: flex;
    gap: .85rem;
    margin-bottom: 1rem;
    padding: 1rem;
    border: 2px solid #c2410c;
    border-radius: .75rem;
    color: #7c2d12;
    background: #fff7ed;
}
.unresolved-resolver__incident > i { margin-top: .2rem; font-size: 1.45rem; }
.unresolved-resolver__incident p { margin: .25rem 0 0; }
.unresolved-resolver__step {
    margin-top: 1rem;
    padding: 1rem;
    border: 1px solid #d8e2ee;
    border-radius: .8rem;
    background: #f8fafc;
}
.unresolved-resolver__step > header { display: flex; gap: .75rem; align-items: flex-start; }
.unresolved-resolver__step > header > span {
    display: grid;
    flex: 0 0 2rem;
    width: 2rem;
    height: 2rem;
    place-items: center;
    border-radius: 50%;
    color: #fff;
    background: #1769aa;
    font-weight: 800;
}
.unresolved-resolver__step h3,
.unresolved-resolver__step h4 { margin: 0; }
.unresolved-resolver__step header p { margin: .2rem 0 .8rem; color: #64748b; }
.unresolved-resolver__suggestion {
    display: flex;
    gap: .65rem;
    margin: .35rem 0 .8rem 2.75rem;
    padding: .7rem .8rem;
    border-left: 4px solid #0284c7;
    background: #e0f2fe;
}
.unresolved-resolver__suggestion strong,
.unresolved-resolver__suggestion span { display: block; }
.unresolved-resolver__suggestion span { color: #475569; font-size: .84rem; }
.unresolved-resolver__search { display: grid; grid-template-columns: 1fr auto; gap: .6rem; }
.unresolved-resolver__search .form-control,
.unresolved-resolver__search .btn { min-height: 3rem; }
.unresolved-resolver__results { display: grid; gap: .5rem; margin-top: .7rem; }
.unresolved-resolver__results > button {
    display: grid;
    grid-template-columns: 1.5rem 1fr auto;
    gap: .6rem;
    align-items: center;
    width: 100%;
    padding: .75rem;
    border: 2px solid #cbd5e1;
    border-radius: .65rem;
    background: #fff;
    text-align: left;
}
.unresolved-resolver__results > button:hover,
.unresolved-resolver__results > button.is-selected { border-color: #1769aa; background: #eff6ff; }
.unresolved-resolver__results strong,
.unresolved-resolver__results small { display: block; }
.unresolved-resolver__results small { color: #64748b; }
.unresolved-resolver__result-check { color: #1769aa; font-size: 1.2rem; }
.unresolved-resolver__empty,
.unresolved-resolver__loading { padding: 1rem; color: #64748b; text-align: center; }
.unresolved-resolver__missing-card {
    display: flex;
    align-items: flex-start;
    gap: .75rem;
    margin-top: .8rem;
    padding: 1rem;
    border: 2px solid #c2410c;
    border-left-width: 5px;
    border-radius: .65rem;
    color: #7c2d12;
    background: #fff7ed;
    font-size: 1rem;
    line-height: 1.5;
}
.unresolved-resolver__missing-card > i { flex: 0 0 auto; margin-top: .15rem; font-size: 1.5rem; }
.unresolved-resolver__missing-card > div { min-width: 0; }
.unresolved-resolver__missing-card-title { display: block; font-size: 1.1rem; font-weight: 700; }
.unresolved-resolver__missing-card ol { margin: .6rem 0 0; padding-left: 1.25rem; }
.unresolved-resolver__missing-card li + li { margin-top: .5rem; }
.unresolved-resolver__loading { display: flex; justify-content: center; gap: .6rem; align-items: center; }
.unresolved-resolver__identity-check {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: .7rem;
    align-items: center;
    padding: .8rem;
    border-radius: .65rem;
    background: #fff;
}
.unresolved-resolver__identity-check > div { padding: .6rem; border: 1px solid #d8e2ee; border-radius: .5rem; }
.unresolved-resolver__identity-check small,
.unresolved-resolver__identity-check strong { display: block; }
.unresolved-resolver__identity-check small { color: #64748b; }
.unresolved-resolver__section-heading {
    display: flex;
    justify-content: space-between;
    gap: .8rem;
    align-items: center;
    margin-top: 1rem;
}
.unresolved-resolver__section-heading span,
.unresolved-resolver__comparison-title span {
    padding: .25rem .5rem;
    border-radius: .35rem;
    font-size: .78rem;
    font-weight: 700;
}
.unresolved-resolver .is-conflict { color: #991b1b; background: #fee2e2; }
.unresolved-resolver .is-ready { color: #166534; background: #dcfce7; }
.unresolved-resolver__comparison-list { display: grid; gap: .5rem; margin-top: .55rem; }
.unresolved-resolver__comparison-list article { padding: .7rem; border: 1px solid #d8e2ee; border-radius: .55rem; background: #fff; }
.unresolved-resolver__comparison-list article.is-conflict { border: 2px solid #dc2626; }
.unresolved-resolver__comparison-title { display: flex; justify-content: space-between; gap: .5rem; align-items: center; }
.unresolved-resolver__choices { display: grid; grid-template-columns: 1fr 1fr; gap: .6rem; margin-top: .55rem; }
.unresolved-resolver__choices button {
    min-height: 4.25rem;
    padding: .65rem;
    border: 2px solid #cbd5e1;
    border-radius: .55rem;
    background: #fff;
    text-align: left;
}
.unresolved-resolver__choices button:hover,
.unresolved-resolver__choices button.is-selected { border-color: #1769aa; background: #dbeafe; box-shadow: 0 0 0 2px rgba(23, 105, 170, .15); }
.unresolved-resolver__choices small,
.unresolved-resolver__choices strong { display: block; }
.unresolved-resolver__choices small { margin-bottom: .2rem; color: #64748b; }
.unresolved-resolver__automatic-value { margin-top: .45rem; padding: .65rem; border-radius: .45rem; background: #fff; }
.unresolved-resolver__automatic-value > span { display: block; margin-bottom: .45rem; color: #475569; }
.unresolved-resolver__characteristics { display: grid; gap: .35rem; width: 100%; }
.unresolved-resolver__characteristics > span {
    display: flex;
    justify-content: space-between;
    gap: .75rem;
    align-items: center;
    padding: .45rem .55rem;
    border: 1px solid #d8e2ee;
    border-radius: .4rem;
    background: #f8fafc;
}
.unresolved-resolver__characteristics b { font-weight: 600; }
.unresolved-resolver__characteristics strong {
    flex: 0 0 auto;
    min-width: 3.4rem;
    padding: .15rem .45rem;
    border-radius: 999px;
    color: #334155;
    background: #e2e8f0;
    text-align: center;
}
.unresolved-resolver__characteristics strong.is-yes { color: #166534; background: #dcfce7; }
.unresolved-resolver__characteristics strong.is-no { color: #991b1b; background: #fee2e2; }
.unresolved-resolver__finish {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    align-items: center;
    padding: .25rem 0;
}
.unresolved-resolver__finish strong,
.unresolved-resolver__finish small { display: block; }
.unresolved-resolver__finish small { margin-top: .2rem; color: #64748b; }
@media (max-width: 767px) {
    .unresolved-resolver__search,
    .unresolved-resolver__choices,
    .unresolved-resolver__identity-check { grid-template-columns: 1fr; }
    .unresolved-resolver__identity-check > i { transform: rotate(90deg); text-align: center; }
    .unresolved-resolver__results > button { grid-template-columns: 1.5rem 1fr; }
    .unresolved-resolver__results .badge { grid-column: 2; white-space: normal; }
    .unresolved-resolver__finish { align-items: stretch; flex-direction: column; }
    .unresolved-resolver__finish .btn { width: 100%; }
    .unresolved-resolver__suggestion { margin-left: 0; }
}
</style>

<style scoped src="./timesheet-status-badges.css"></style>
